import { useState, forwardRef, useImperativeHandle } from 'react';
import { Modal, Table } from 'antd';
import type { TableColumnType } from 'antd';
import { getReviewHistory } from '@/api/contentReview';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { FORMAT_TIME } from '@/types/common';
import { ReviewHistory } from '@/types/contentReview';

export interface HistoryDialogRef {
  show: (id: string) => void;
}

const HistoryDialog = forwardRef<HistoryDialogRef>((_props, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [artworkId, setArtworkId] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15;

  const { data: history, isFetching } = useQuery({
    queryKey: ['reviewHistory', currentPage],
    queryFn: async () => {
      const resp = await getReviewHistory({
        artworkId,
        currentPage,
        pageSize,
      });
      return resp.data;
    },
    enabled: !!artworkId,
  });

  const columns: TableColumnType<ReviewHistory>[] = [
    {
      title: 'Operation Time',
      key: 'reviewTime',
      render: (_, record) => {
        return <div>{dayjs(record.createTime).format(FORMAT_TIME)}</div>;
      },
    },
    {
      title: 'Operation',
      dataIndex: 'operateTypeDesc',
      key: 'operateTypeDesc',
    },
    {
      title: 'Status/Item',
      dataIndex: 'operateResultDesc',
      key: 'operateResultDesc',
    },
    {
      title: 'User',
      dataIndex: 'operateUserName',
      key: 'operateUserName',
    },
  ];

  const handleClose = () => {
    setIsOpen(false);
    setArtworkId('');
    setCurrentPage(1);
  };

  const handleShow = (id: string) => {
    setIsOpen(true);
    setArtworkId(id);
  };

  useImperativeHandle(ref, () => ({
    show: handleShow,
  }));

  return (
    <Modal
      title="Operation Record"
      open={isOpen}
      width={800}
      onCancel={handleClose}
      footer={null}
      destroyOnClose
    >
      <Table
        rowKey="id"
        size="small"
        bordered
        columns={columns}
        dataSource={history?.records || []}
        pagination={{
          pageSize,
          current: currentPage,
          total: history?.total || 0,
          onChange: (page) => setCurrentPage(page),
          showSizeChanger: false,
        }}
        loading={isFetching}
      />
    </Modal>
  );
});

HistoryDialog.displayName = 'HistoryDialog';

export default HistoryDialog;
